import { useApolloClient } from '@apollo/client';
import { CandidateProfileTalentListTableColumns } from 'modules/candidate-profile/tables/types/CandidateProfileTalentListTable';
import { ActionOption, ActionPopover } from 'components/ActionPopover';
import { useModalV2 } from 'common/modal/hooks/useModalV2';
import {
  useUpdateCandidateProfileListMutation,
  useRemoveCandidateProfileListMutation,
  SearchCandidateProfileListsDocument
} from 'generated-graphql/hooks/apollo-hooks';
import { UpsertTalentPoolFormData } from 'modules/candidate-profile/forms/schema';
import { ListTeam } from 'generated-graphql/types';
import { getTalentListForm } from 'modules/candidate-profile/utils/getTalentListForm';
import { useToast } from 'services/toast/hooks';
import { ToastType } from 'services/toast/types';
import { usePathsV2 } from 'core/services/navigation/usePathsV2';
import {
  CandidateProfileListProps,
  CandidateProfileListType
} from 'modules/candidate-profile/types/CandidateProfileListProps';
import { waitForOpenSearchAndWeaviate } from 'modules/candidate-profile/utils/waitForOpenSearchAndWeaviate';

type UseCandidateProfileTalentPoolTableActionsProps = {
  candidateProfileListProps: CandidateProfileListProps;
  updateItem: (
    id: string,
    updates: UpsertTalentPoolFormData,
    teams: ListTeam[]
  ) => void;
  removeItem: (id: string) => void;
};

export const useCandidateProfileTalentPoolTableActions = ({
  candidateProfileListProps,
  updateItem,
  removeItem
}: UseCandidateProfileTalentPoolTableActionsProps) => {
  const apolloClient = useApolloClient();
  const { openModal } = useModalV2();
  const { showToast } = useToast();
  const [updateCandidateProfileList] = useUpdateCandidateProfileListMutation();
  const [removeCandidateProfileList] = useRemoveCandidateProfileListMutation();
  const { navigateToPath } = usePathsV2();

  const shouldShowViewCandidates = () => {
    switch (candidateProfileListProps.type) {
      case CandidateProfileListType.CANDIDATES_LIST:
      case CandidateProfileListType.ANALYSIS_CANDIDATES_LIST:
        return true;
      case CandidateProfileListType.CANDIDATES_FROM_TALENT_POOL:
      case CandidateProfileListType.ADD_CANDIDATES_TO_TALENT_POOL:
      case CandidateProfileListType.ANALYSIS_CANDIDATES_FROM_TALENT_POOL:
        return false;
    }
  };

  const shouldShowViewCandidatesValue = shouldShowViewCandidates();

  const handleViewCandidates = (id: string) => {
    switch (candidateProfileListProps.type) {
      case CandidateProfileListType.CANDIDATES_LIST:
        navigateToPath.TALENT_POOL_DETAILS({
          talentPoolId: id
        });
        break;
      case CandidateProfileListType.ANALYSIS_CANDIDATES_LIST:
        navigateToPath.ANALYSE_TALENT_POOL_DETAILS({
          candidateBatchId: candidateProfileListProps.candidateBatchId,
          talentPoolId: id
        });
        break;
      case CandidateProfileListType.CANDIDATES_FROM_TALENT_POOL:
      case CandidateProfileListType.ADD_CANDIDATES_TO_TALENT_POOL:
      case CandidateProfileListType.ANALYSIS_CANDIDATES_FROM_TALENT_POOL:
        // This won't happen
        break;
    }
  };

  const handleEditAction = async (
    dataId: string,
    formData: UpsertTalentPoolFormData,
    sendsTeams: boolean
  ) => {
    try {
      const { data: updateData } = await updateCandidateProfileList({
        variables: {
          input: {
            id: dataId,
            title: formData.name,
            description: formData.description,
            ...(sendsTeams && { teamIds: formData.teamIds })
          }
        }
      });

      updateItem(
        dataId,
        formData,
        updateData?.updateCandidateProfileListForUser.teams ?? []
      );

      showToast({
        title: 'Talent list updated',
        type: ToastType.Success
      });
    } catch {
      showToast({
        title: 'Failed to update talent list',
        type: ToastType.Error
      });
    }
  };

  const handleDeleteAction = async (candidateProfileListId: string) => {
    try {
      await removeCandidateProfileList({
        variables: {
          input: {
            candidateProfileListId
          }
        }
      });

      removeItem(candidateProfileListId);

      await waitForOpenSearchAndWeaviate();
      await apolloClient.refetchQueries({
        include: [SearchCandidateProfileListsDocument]
      });

      showToast({
        title: 'Talent list deleted',
        type: ToastType.Success
      });

      // Redirect to listing page when deleting from details page
      switch (candidateProfileListProps.type) {
        case CandidateProfileListType.CANDIDATES_FROM_TALENT_POOL:
          navigateToPath.CANDIDATE_PROFILE_LIST({});
          break;
        case CandidateProfileListType.ANALYSIS_CANDIDATES_FROM_TALENT_POOL:
          navigateToPath.ANALYSE_CANDIDATE_PROFILE_LIST({
            candidateBatchId: candidateProfileListProps.candidateBatchId
          });
          break;
      }
    } catch {
      showToast({
        title: 'Failed to delete talent list',
        type: ToastType.Error
      });
    }
  };

  const actionsRenderer = (data: CandidateProfileTalentListTableColumns) => {
    const options: ActionOption[] = [
      ...(shouldShowViewCandidatesValue
        ? [
            {
              label: 'View candidates',
              onClick: () => handleViewCandidates(data.id),
              itemColor: 'primary.main'
            }
          ]
        : []),
      {
        label: 'Edit details',
        onClick: () => {
          const { schema, fields, defaultTeamIds, teamState } =
            getTalentListForm(data.teams);

          openModal({
            width: '450px',
            title: 'Edit talent list',
            primaryButtonText: 'Confirm',
            secondaryButtonText: 'Cancel',
            form: {
              schema,
              fields,
              defaultValues: {
                name: data.title,
                teamIds: defaultTeamIds,
                description: data.description ?? ''
              }
            },
            onPrimaryAction: async (formData: UpsertTalentPoolFormData) => {
              await handleEditAction(data.id, formData, teamState.sendsTeams);
            }
          });
        },
        itemColor: 'primary.main'
      },
      {
        label: 'Delete',
        onClick: () =>
          openModal({
            title: 'Delete talent list',
            subtitle: 'This cannot be undone.',
            primaryButtonText: 'Cancel',
            secondaryButtonText: 'Delete',
            primaryButtonProps: { variant: 'primary' },
            secondaryButtonProps: { variant: 'tertiary', state: 'destructive' },
            onSecondaryAction: async () => await handleDeleteAction(data.id)
          }),
        itemColor: 'error.main'
      }
    ];

    return <ActionPopover id={data.id} options={options} />;
  };

  return {
    actionsRenderer
  };
};
